import { ChangeEvent, useMemo, useRef, useState } from 'react';
import type { DocumentTagDto } from '@sib/api';
import { createUseAppTranslation } from '@sib/i18n';
import { TagErrorEnum } from '../utils/common';

const useDocumentsTranslation = createUseAppTranslation('documents');

interface UseTagsBaseProps {
  selectedTags: string[];
  allTagsData: DocumentTagDto[];
}

export const useTagsBase = ({
  selectedTags,
  allTagsData,
}: UseTagsBaseProps) => {
  const { t } = useDocumentsTranslation();
  const commandListRef = useRef<HTMLDivElement>(null);
  const [newTagName, setNewTagName] = useState('');
  const [tagError, setTagError] = useState<TagErrorEnum | null>(null);

  const tags = useMemo(
    () =>
      allTagsData.map(tag => ({
        uuid: tag.uuid,
        name: tag.name,
        isChecked: selectedTags.includes(tag.uuid),
        isPredefined: tag.isPredefined,
      })),
    [allTagsData, selectedTags]
  );

  const customTags = useMemo(
    () => tags.filter(tag => !tag.isPredefined),
    [tags]
  );

  const predefinedTags = useMemo(
    () => tags.filter(tag => tag.isPredefined),
    [tags]
  );

  const tagLabel = useMemo(() => {
    if (selectedTags.length === 0)
      return t('components.tagSelector.emptyAddTagsPlaceholder');

    const selectedTagNames = allTagsData
      .filter(tag => selectedTags.includes(tag.uuid))
      .map(tag => tag.name);

    if (selectedTagNames.length === 1) return selectedTagNames[0];

    return [selectedTagNames[0], selectedTagNames.length - 1];
  }, [selectedTags, allTagsData, t]);

  const handleTagNameChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    setNewTagName(value);

    if (value.length > 22) return setTagError(TagErrorEnum.tagChars);

    setTagError(null);
  };

  const validateNewTag = () => {
    if (newTagName.trim() === '' || tagError) return false;

    const isTagExists = allTagsData.some(
      tag => tag.name.toLowerCase() === newTagName.trim().toLowerCase()
    );

    if (isTagExists) {
      setTagError(TagErrorEnum.tagExists);

      return false;
    }

    return true;
  };

  const scrollToTop = () => {
    if (commandListRef.current) {
      commandListRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const resetNewTagName = () => {
    setNewTagName('');
    setTagError(null);
  };

  return {
    tags,
    customTags,
    predefinedTags,
    tagLabel,
    commandListRef,
    newTagName,
    tagError,
    tagErrorEnum: TagErrorEnum,
    handleTagNameChange,
    validateNewTag,
    scrollToTop,
    resetNewTagName,
  };
};

export type UseTagsBaseReturn = ReturnType<typeof useTagsBase>;
